import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, type ApiKeyRow, type Provider, type PluginSetting, type VoiceConfig, type ExtensionInfo, type RecognizerInfo, type FeaturePluginInfo } from '../api/client'
import { Icon, type IconName } from '../components/Icon'
import { ToggleSwitch } from '../components/ToggleSwitch'
import { useTheme } from '../hooks/useTheme'
import { useAuth } from '../hooks/useAuth'


type SettingsTab = 'general' | 'appearance' | 'agents' | 'providers' | 'account'
const TABS: { id: SettingsTab; label: string; icon: IconName }[] = [
  { id: 'general',    label: 'General',   icon: 'settings' },
  { id: 'appearance', label: 'Appearance', icon: 'sun' },
  { id: 'agents',     label: 'Agents',    icon: 'terminal' },
  { id: 'providers',  label: 'Providers', icon: 'spark' },
  { id: 'account',    label: 'Account',   icon: 'logout' },
]

export function SettingsPage() {
  const navigate = useNavigate()
  const { theme, toggle: toggleTheme } = useTheme()
  const { logout } = useAuth()
  const [activeTab, setActiveTab] = useState<SettingsTab>('general')
  // Mobile only: whether we've drilled into a category (list -> page). Desktop
  // ignores this (CSS keeps both nav + panel visible).
  const [drilled, setDrilled] = useState(false)
  const [keys, setKeys] = useState<ApiKeyRow[]>([])
  const [newName, setNewName] = useState('')
  const [newValue, setNewValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Providers (from DB)
  const [providers, setProviders] = useState<Provider[]>([])

  // Provider keys (live_call / tts / stt)
  const [liveCallKeys, setLiveCallKeys] = useState<ApiKeyRow[]>([])
  const [ttsKeys, setTtsKeys] = useState<ApiKeyRow[]>([])
  const [sttKeys, setSttKeys] = useState<ApiKeyRow[]>([])
  const [chatProviderModel, setChatProviderModel] = useState('')
  const [liveProviderModel, setLiveProviderModel] = useState('')
  const [liveNickDraft, setLiveNickDraft] = useState('')
  const [liveApiKeyDraft, setLiveApiKeyDraft] = useState('')
  const [ttsProviderModel, setTtsProviderModel] = useState('')
  const [ttsNickDraft, setTtsNickDraft] = useState('')
  const [ttsApiKeyDraft, setTtsApiKeyDraft] = useState('')
  const [sttProviderModel, setSttProviderModel] = useState('')
  const [sttNickDraft, setSttNickDraft] = useState('')
  const [sttApiKeyDraft, setSttApiKeyDraft] = useState('')
  const [savingProvider, setSavingProvider] = useState<string | null>(null)
  const [liveSelected, setLiveSelected] = useState('')
  const [ttsSelected, setTtsSelected] = useState('')
  const [sttSelected, setSttSelected] = useState('')
  const [openDropdown, setOpenDropdown] = useState<'live_call' | 'tts' | 'stt' | null>(null)

  // App settings
  const [defaultAgent, setDefaultAgent] = useState<string>('bash')
  const [savingDefault, setSavingDefault] = useState(false)

  // Plugin CWDs
  const [pluginSettings, setPluginSettings] = useState<PluginSetting[]>([])
  const [cwdDrafts, setCwdDrafts] = useState<Record<string, string>>({})
  const [savingPlugin, setSavingPlugin] = useState<string | null>(null)

  // External plugins / extensions (FB-8)
  const [extensions, setExtensions] = useState<ExtensionInfo[]>([])
  const [togglingExt, setTogglingExt] = useState<string | null>(null)

  // Internal plugins / recognizers (PLUGIN_MODULES.md Phase 1)
  const [recognizers, setRecognizers] = useState<RecognizerInfo[]>([])
  const [togglingRec, setTogglingRec] = useState<string | null>(null)

  // Internal feature plugins (voice, …) (PLUGIN_MODULES.md Phase 2)
  const [features, setFeatures] = useState<FeaturePluginInfo[]>([])
  const [togglingFeature, setTogglingFeature] = useState<string | null>(null)

  // Voice / AI providers
  const [voiceConfig, setVoiceConfig] = useState<VoiceConfig | null>(null)
  const [notesDirDraft, setNotesDirDraft] = useState('')
  const [savingVoice, setSavingVoice] = useState<string | null>(null)

  // Account-level API key for the machine API (spec/CHAT_API.md §5).
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [apiKeyBusy, setApiKeyBusy] = useState(false)
  useEffect(() => {
    api.account.getApiKey().then(r => setApiKey(r.apiKey)).catch(() => {})
  }, [])
  const genApiKey = async () => {
    setApiKeyBusy(true)
    try { const r = await api.account.genApiKey(); setApiKey(r.apiKey) }
    catch (err) { console.error('[Settings] gen api key error:', err) }
    finally { setApiKeyBusy(false) }
  }
  const clearApiKey = async () => {
    setApiKeyBusy(true)
    try { await api.account.clearApiKey(); setApiKey(null) }
    catch (err) { console.error('[Settings] clear api key error:', err) }
    finally { setApiKeyBusy(false) }
  }

  const loadKeys = () => {
    api.settings.apiKeys.list('chat')
      .then(setKeys)
      .catch(err => console.error('[Settings] load error:', err))
  }

  const loadProviders = () => {
    api.settings.providers.list().then(list => {
      setProviders(list)
      const first = (cat: string) => list.find(p => p.category === cat)?.model ?? ''
      setChatProviderModel(m => m || first('chat'))
      setLiveProviderModel(m => m || first('live_call'))
      setTtsProviderModel(m => m || first('tts'))
      setSttProviderModel(m => m || first('stt'))
    }).catch(() => {})
  }

  const loadProviderKeys = () => {
    api.settings.apiKeys.list('live_call').then(setLiveCallKeys).catch(() => {})
    api.settings.apiKeys.list('tts').then(setTtsKeys).catch(() => {})
    api.settings.apiKeys.list('stt').then(setSttKeys).catch(() => {})
  }

  const loadApp = () => {
    api.settings.app.get()
      .then(s => {
        if (s.defaultAgent) setDefaultAgent(s.defaultAgent)
      })
      .catch(err => console.error('[Settings] app load error:', err))
  }

  const loadPlugins = () => {
    api.settings.plugins.list()
      .then(list => {
        setPluginSettings(list)
        const drafts: Record<string, string> = {}
        for (const p of list) drafts[p.name] = p.currentCwd ?? ''
        setCwdDrafts(drafts)
      })
      .catch(err => console.error('[Settings] plugins load error:', err))
  }

  const loadExtensions = () => {
    api.settings.extensions.list()
      .then(setExtensions)
      .catch(err => console.error('[Settings] extensions load error:', err))
  }

  const loadRecognizers = () => {
    api.settings.recognizers.list()
      .then(setRecognizers)
      .catch(err => console.error('[Settings] recognizers load error:', err))
  }

  const loadFeatures = () => {
    api.settings.featurePlugins.list()
      .then(setFeatures)
      .catch(err => console.error('[Settings] feature-plugins load error:', err))
  }

  const loadVoiceConfig = () => {
    api.settings.voiceConfig()
      .then(cfg => {
        setVoiceConfig(cfg)
        setNotesDirDraft(cfg.notesDir ?? '')
        if (cfg.liveCallActiveKey) setLiveSelected(cfg.liveCallActiveKey)
        if (cfg.ttsActiveKey) setTtsSelected(cfg.ttsActiveKey)
        if (cfg.sttActiveKey) setSttSelected(cfg.sttActiveKey)
      })
      .catch(err => console.error('[Settings] voice config load error:', err))
  }

  useEffect(() => {
    loadKeys()
    loadProviders()
    loadProviderKeys()
    loadApp()
    loadPlugins()
    loadExtensions()
    loadRecognizers()
    loadFeatures()
    loadVoiceConfig()
  }, [])

  const handleAdd = async () => {
    if (!newName.trim() || !newValue.trim()) {
      setError('Name and value are required')
      return
    }
    setSaving(true)
    setError('')
    try {
      await api.settings.apiKeys.set(newName.trim(), newValue.trim(), 'chat', undefined, chatProviderModel || undefined)
      setNewName('')
      setNewValue('')
      loadKeys()
    } catch (err) {
      setError(String(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (name: string) => {
    if (!confirm(`Delete ${name}?`)) return
    try {
      await api.settings.apiKeys.delete(name)
      loadKeys()
    } catch (err) {
      console.error('[Settings] delete error:', err)
    }
  }

  const saveDefaultAgent = async (value: string) => {
    setDefaultAgent(value)
    setSavingDefault(true)
    try {
      await api.settings.app.set('defaultAgent', value)
    } catch (err) {
      console.error('[Settings] defaultAgent save error:', err)
    } finally {
      setSavingDefault(false)
    }
  }

  const savePluginCwd = async (name: string) => {
    const draft = (cwdDrafts[name] ?? '').trim()
    setSavingPlugin(name)
    try {
      await api.settings.plugins.setCwd(name, draft || null)
      loadPlugins()
    } catch (err) {
      console.error('[Settings] plugin cwd save error:', err)
    } finally {
      setSavingPlugin(null)
    }
  }

  const toggleExtension = async (name: string, enabled: boolean) => {
    // Optimistic update
    setExtensions(list => list.map(e => (e.name === name ? { ...e, enabled } : e)))
    setTogglingExt(name)
    try {
      const updated = await api.settings.extensions.setEnabled(name, enabled)
      setExtensions(list => list.map(e => (e.name === name ? { ...e, ...updated } : e)))
      // Enabling/disabling an extension changes the active agent registry.
      loadPlugins()
    } catch (err) {
      console.error('[Settings] extension toggle error:', err)
      setExtensions(list => list.map(e => (e.name === name ? { ...e, enabled: !enabled } : e))) // revert
    } finally {
      setTogglingExt(null)
    }
  }

  const toggleRecognizer = async (name: string, enabled: boolean) => {
    // Optimistic update
    setRecognizers(list => list.map(r => (r.name === name ? { ...r, enabled } : r)))
    setTogglingRec(name)
    try {
      await api.settings.recognizers.setEnabled(name, enabled)
    } catch (err) {
      console.error('[Settings] recognizer toggle error:', err)
      setRecognizers(list => list.map(r => (r.name === name ? { ...r, enabled: !enabled } : r))) // revert
    } finally {
      setTogglingRec(null)
    }
  }

  const toggleFeature = async (id: string, enabled: boolean) => {
    // Optimistic update
    setFeatures(list => list.map(f => (f.id === id ? { ...f, enabled } : f)))
    setTogglingFeature(id)
    try {
      await api.settings.featurePlugins.setEnabled(id, enabled)
    } catch (err) {
      console.error('[Settings] feature toggle error:', err)
      setFeatures(list => list.map(f => (f.id === id ? { ...f, enabled: !enabled } : f))) // revert
    } finally {
      setTogglingFeature(null)
    }
  }

  const addProviderKey = async (category: 'live_call' | 'tts' | 'stt', nick: string, keyVal: string, model: string) => {
    if (!nick.trim() || !keyVal.trim()) return
    setSavingProvider(category)
    try {
      await api.settings.apiKeys.set(nick.trim(), keyVal.trim(), category, nick.trim(), model)
      if (category === 'live_call') { setLiveNickDraft(''); setLiveApiKeyDraft('') }
      else if (category === 'tts') { setTtsNickDraft(''); setTtsApiKeyDraft('') }
      else { setSttNickDraft(''); setSttApiKeyDraft('') }
      loadProviderKeys()
    } catch (err) {
      console.error('[Settings] add provider key error:', err)
    } finally {
      setSavingProvider(null)
    }
  }

  const deleteProviderKey = async (name: string, category: 'live_call' | 'tts' | 'stt') => {
    if (!confirm(`ลบ "${name}"?`)) return
    try {
      await api.settings.apiKeys.delete(name)
      if (voiceConfig?.liveCallActiveKey === name) await api.settings.app.set('liveCallActiveKey', '')
      if (voiceConfig?.ttsActiveKey === name) await api.settings.app.set('ttsActiveKey', '')
      if (voiceConfig?.sttActiveKey === name) await api.settings.app.set('sttActiveKey', '')
      loadProviderKeys()
      loadVoiceConfig()
    } catch (err) {
      console.error('[Settings] delete provider key error:', err)
    }
  }

  const setActiveProviderKey = async (category: 'live_call' | 'tts' | 'stt', name: string) => {
    const settingKey = category === 'live_call' ? 'liveCallActiveKey' : category === 'tts' ? 'ttsActiveKey' : 'sttActiveKey'
    try {
      await api.settings.app.set(settingKey, name)
      loadVoiceConfig()
    } catch (err) {
      console.error('[Settings] set active key error:', err)
    }
  }

  const saveNotesDir = async () => {
    setSavingVoice('notesDir')
    try {
      await api.settings.app.set('notesDir', notesDirDraft.trim())
      loadVoiceConfig()
    } catch (err) {
      console.error('[Settings] notesDir save error:', err)
    } finally {
      setSavingVoice(null)
    }
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button onClick={() => navigate('/')} className="btn-ghost">
          ← Home
        </button>
        <h1>Settings</h1>
      </div>

      <div className={`settings-layout${drilled ? ' drilled' : ''}`}>
        <nav className="settings-nav">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`settings-nav-btn${activeTab === t.id ? ' active' : ''}`}
              onClick={() => { setActiveTab(t.id); setDrilled(true) }}
            >
              <Icon name={t.icon} size={16} />
              <span className="settings-nav-label">{t.label}</span>
              <Icon name="chevron-right" size={16} className="settings-nav-chevron" />
            </button>
          ))}
        </nav>

        <div className="settings-content">
          <button className="settings-back btn-ghost" onClick={() => setDrilled(false)}>
            <Icon name="chevron-left" size={16} /> Settings
          </button>

        {activeTab === 'general' && (<>
        {/* ─── Default agent ──────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>Default Agent</h2>
          <p className="settings-hint">
            Pre-selected when you open the "New Chat" dialog.
          </p>
          <select
            className="modal-input"
            value={defaultAgent}
            onChange={e => saveDefaultAgent(e.target.value)}
            disabled={savingDefault}
          >
            {pluginSettings.map(p => (
              <option key={p.name} value={p.name}>{p.displayName}</option>
            ))}
          </select>
        </section>

        </>)}

        {activeTab === 'appearance' && (<>
        {/* ─── Theme ──────────────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>Theme</h2>
          <p className="settings-hint">
            โหมดสว่าง / มืด จำค่าต่อเครื่องนี้ (ค่าเริ่มต้น: มืด).
          </p>
          <label className="settings-toggle-row" style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <button
              type="button"
              role="switch"
              aria-checked={theme === 'dark'}
              className="theme-switch"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              <Icon name="sun" size={12} className="theme-switch-ico theme-switch-ico--sun" />
              <Icon name="moon" size={12} className="theme-switch-ico theme-switch-ico--moon" />
              <span className="theme-switch-knob" />
            </button>
            <span>{theme === 'dark' ? 'Dark mode' : 'Light mode'}</span>
          </label>
        </section>
        </>)}

        {activeTab === 'agents' && (<>
        {/* ─── Per-plugin storage location ────────────────────────────────── */}
        <section className="settings-section">
          <h2>Agents · Storage Locations</h2>
          <p className="settings-hint">
            Each built-in agent stores its session files in a working directory. The default
            matches where the agent's npm CLI would run from your terminal. Override only if
            you want sessions stored under a different project root.
          </p>

          {pluginSettings.map(p => (
            <div key={p.name} className="settings-plugin-row">
              <div className="settings-plugin-name">{p.displayName}</div>
              <div className="settings-plugin-cwd-row">
                <input
                  className="modal-input settings-plugin-cwd-input"
                  placeholder={p.defaultCwd ?? '$HOME'}
                  value={cwdDrafts[p.name] ?? ''}
                  onChange={e => setCwdDrafts(d => ({ ...d, [p.name]: e.target.value }))}
                />
                <button
                  className="btn-primary"
                  onClick={() => savePluginCwd(p.name)}
                  disabled={savingPlugin === p.name}
                >
                  {savingPlugin === p.name ? '...' : 'Save'}
                </button>
              </div>
              <div className="settings-plugin-meta">
                Default: <code>{p.defaultCwd ?? '$HOME'}</code>
                {p.currentCwd && (
                  <>
                    {'  ·  '}Active: <code>{p.currentCwd}</code>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* ─── Internal plugins / recognizers (PLUGIN_MODULES.md Phase 1) ──── */}
        <section className="settings-section">
          <h2>Internal Plugins</h2>
          <p className="settings-hint">
            Built-in modules you can turn on and off. Features are larger add-ons (like the voice
            panel); recognizers turn raw agent output into structured cards. Changes apply to new
            chats (reopen a chat to apply).
          </p>

          {/* Features (voice, …) — Phase 2 */}
          <h3 style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 8px', color: 'var(--text)' }}>Features</h3>
          {features.map(f => (
            <div key={f.id} className="settings-plugin-row">
              <div
                className="settings-plugin-name"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
              >
                <span>{f.displayName}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 20, textAlign: 'right' }}>
                    {togglingFeature === f.id ? '…' : f.enabled ? 'On' : 'Off'}
                  </span>
                  <ToggleSwitch
                    checked={f.enabled}
                    disabled={togglingFeature === f.id}
                    onChange={v => toggleFeature(f.id, v)}
                    ariaLabel={`${f.displayName} ${f.enabled ? 'on' : 'off'}`}
                  />
                </span>
              </div>
              {f.description && (
                <div className="settings-plugin-meta" style={{ whiteSpace: 'pre-wrap' }}>{f.description}</div>
              )}
            </div>
          ))}

          {/* Recognizers — Phase 1 */}
          <h3 style={{ fontSize: 13, fontWeight: 600, margin: '20px 0 8px', color: 'var(--text)' }}>Recognizers</h3>
          <p className="settings-hint" style={{ marginTop: 0 }}>
            Turn raw agent output into structured cards (tables, tool calls, status chips, menus).
            Turn one off to fall back to the plain text mirror. Load-bearing modules can't be turned off.
          </p>

          {recognizers.map(rec => (
            <div key={rec.name} className="settings-plugin-row">
              <div
                className="settings-plugin-name"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
              >
                <span>
                  {rec.displayName}
                  {rec.mandatory && (
                    <span className="settings-key-preview" style={{ marginLeft: 8, opacity: 0.7 }}>required</span>
                  )}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 20, textAlign: 'right' }}>
                    {togglingRec === rec.name ? '…' : rec.enabled ? 'On' : 'Off'}
                  </span>
                  <ToggleSwitch
                    checked={rec.enabled}
                    disabled={rec.mandatory || togglingRec === rec.name}
                    onChange={v => toggleRecognizer(rec.name, v)}
                    ariaLabel={`${rec.displayName} ${rec.enabled ? 'on' : 'off'}`}
                  />
                </span>
              </div>
              <div className="settings-plugin-meta">
                <code>{rec.name}</code>
              </div>
            </div>
          ))}
        </section>

        {/* ─── External plugins / extensions (FB-8) ───────────────────────── */}
        <section className="settings-section">
          <h2>External Plugins (Extension)</h2>
          <p className="settings-hint">
            Community agents loaded from disk (<code>plugins/external/</code>). They show up as
            agent options in New Chat just like the built-ins (an extension that declares a
            structured <code>historyFormat</code> gets the clean chat view too). Turning one off
            removes it from the New Chat agent list (existing chats are unaffected).
          </p>

          {extensions.length === 0 && (
            <p className="settings-empty" style={{ lineHeight: 1.6 }}>
              ยังไม่มี extension. วางโฟลเดอร์ extension ไว้ที่{' '}
              <code>plugins/external/&lt;name&gt;/extension.md</code> แล้วรีเฟรชหน้านี้.
            </p>
          )}

          {extensions.map(ext => (
            <div key={ext.name} className="settings-plugin-row">
              <div
                className="settings-plugin-name"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
              >
                <span>
                  {ext.displayName || ext.name}
                  {ext.version && (
                    <span className="settings-key-preview" style={{ marginLeft: 8 }}>v{ext.version}</span>
                  )}
                  {!ext.enabled && (
                    <span className="settings-key-preview" style={{ marginLeft: 8, opacity: 0.7 }}>disabled</span>
                  )}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 20, textAlign: 'right' }}>
                    {togglingExt === ext.name ? '…' : ext.enabled ? 'On' : 'Off'}
                  </span>
                  <ToggleSwitch
                    checked={ext.enabled}
                    disabled={togglingExt === ext.name}
                    onChange={v => toggleExtension(ext.name, v)}
                    ariaLabel={`${ext.displayName || ext.name} ${ext.enabled ? 'on' : 'off'}`}
                  />
                </span>
              </div>
              {ext.description && (
                <div className="settings-plugin-meta" style={{ whiteSpace: 'pre-wrap' }}>
                  {ext.description}
                </div>
              )}
              <div className="settings-plugin-meta">
                <code>{ext.name}</code>
                {ext.author && <>{'  ·  '}by {ext.author}</>}
              </div>
            </div>
          ))}
        </section>
        </>)}

        {activeTab === 'providers' && (<>
        {/* ─── API Keys ───────────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>API Keys</h2>
          <p className="settings-hint">
            Keys are stored on the server and used for API mode connections.
          </p>

          {keys.length === 0 && (
            <p className="settings-empty">No API keys stored.</p>
          )}

          {keys.map(k => (
            <div key={k.name} className="settings-key-row">
              <div className="settings-key-info">
                <span className="settings-key-name">{k.name}</span>
                <span className="settings-key-preview">{k.model ? `${k.model} · ` : ''}{k.preview}</span>
              </div>
              <button className="settings-key-delete btn-ghost" onClick={() => handleDelete(k.name)}>Remove</button>
            </div>
          ))}

          <div className="settings-add-form">
            <select className="modal-input" value={chatProviderModel} onChange={e => setChatProviderModel(e.target.value)}>
              {providers.filter(p => p.category === 'chat').map(p => (
                <option key={p.id} value={p.model}>{p.label} — {p.model}</option>
              ))}
            </select>
            <input className="modal-input" placeholder="Key name (e.g. ANTHROPIC_API_KEY)" value={newName} onChange={e => setNewName(e.target.value)} />
            <input className="modal-input" type="password" placeholder="Value (sk-ant-...)" value={newValue} onChange={e => setNewValue(e.target.value)} />
            {error && <span className="settings-error">{error}</span>}
            <button className="btn-primary" onClick={handleAdd} disabled={saving}>
              {saving ? '...' : 'Add Key'}
            </button>
          </div>
        </section>

        {/* ─── Live Call ──────────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>Live Call</h2>
          <p className="settings-hint">API keys สำหรับโทรคุยด้วยเสียงแบบ real-time</p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <div
                className="modal-input"
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
                onClick={() => setOpenDropdown(openDropdown === 'live_call' ? null : 'live_call')}
              >
                <span style={{ color: liveSelected ? 'inherit' : 'var(--text-muted, #888)' }}>
                  {liveSelected ? (liveCallKeys.find(k => k.name === liveSelected)?.nickname ?? liveSelected) : '— เลือก key —'}
                  {liveSelected && voiceConfig?.liveCallActiveKey === liveSelected && <span style={{ color: 'var(--accent, #4ade80)', marginLeft: 8, fontSize: 12 }}>Active ✓</span>}
                </span>
                <span style={{ fontSize: 11, opacity: 0.5 }}>▾</span>
              </div>
              {openDropdown === 'live_call' && <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpenDropdown(null)} />
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-elevated, #1a1a1a)', border: '1px solid var(--border, rgba(255,255,255,0.1))', borderRadius: 8, marginTop: 4, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                  {liveCallKeys.length === 0 && <div style={{ padding: '10px 14px', color: 'var(--text-muted, #888)', fontSize: 13 }}>ยังไม่มี key — เพิ่มด้านล่าง</div>}
                  {liveCallKeys.map(k => (
                    <div key={k.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: liveSelected === k.name ? 'rgba(255,255,255,0.07)' : 'transparent' }}
                      onMouseEnter={e => { if (liveSelected !== k.name) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.04)' }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = liveSelected === k.name ? 'rgba(255,255,255,0.07)' : 'transparent' }}>
                      <div style={{ flex: 1 }} onClick={() => { setLiveSelected(k.name); setOpenDropdown(null) }}>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{k.nickname ?? k.name}</span>
                        <span style={{ color: 'var(--text-muted, #888)', fontSize: 12, marginLeft: 8 }}>{k.model ?? ''} · {k.preview}</span>
                      </div>
                      <button onClick={e => { e.stopPropagation(); deleteProviderKey(k.name, 'live_call') }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted, #666)', cursor: 'pointer', padding: '2px 6px', fontSize: 16, lineHeight: 1, borderRadius: 4 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted, #666)')}>×</button>
                    </div>
                  ))}
                </div>
              </>}
            </div>
            <button className="btn-primary" style={{ whiteSpace: 'nowrap' }}
              onClick={() => liveSelected && setActiveProviderKey('live_call', liveSelected)}
              disabled={!liveSelected || liveSelected === voiceConfig?.liveCallActiveKey}>
              Save
            </button>
          </div>

          <div className="settings-add-form">
            <select className="modal-input" value={liveProviderModel} onChange={e => setLiveProviderModel(e.target.value)}>
              {providers.filter(p => p.category === 'live_call').map(p => (
                <option key={p.id} value={p.model}>{p.label} — {p.model}</option>
              ))}
            </select>
            <input className="modal-input" placeholder="Nickname (e.g. Work Key)" value={liveNickDraft} onChange={e => setLiveNickDraft(e.target.value)} />
            <input className="modal-input" type="password" placeholder="API Key (AIza...)" value={liveApiKeyDraft} onChange={e => setLiveApiKeyDraft(e.target.value)} />
            <button className="btn-primary" onClick={() => addProviderKey('live_call', liveNickDraft, liveApiKeyDraft, liveProviderModel)} disabled={savingProvider === 'live_call'}>
              {savingProvider === 'live_call' ? '...' : 'Add Key'}
            </button>
          </div>
        </section>

        {/* ─── TTS ────────────────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>TTS</h2>
          <p className="settings-hint">API keys สำหรับแปลงข้อความเป็นเสียง</p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <div className="modal-input" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
                onClick={() => setOpenDropdown(openDropdown === 'tts' ? null : 'tts')}>
                <span style={{ color: ttsSelected ? 'inherit' : 'var(--text-muted, #888)' }}>
                  {ttsSelected ? (ttsKeys.find(k => k.name === ttsSelected)?.nickname ?? ttsSelected) : '— เลือก key —'}
                  {ttsSelected && voiceConfig?.ttsActiveKey === ttsSelected && <span style={{ color: 'var(--accent, #4ade80)', marginLeft: 8, fontSize: 12 }}>Active ✓</span>}
                </span>
                <span style={{ fontSize: 11, opacity: 0.5 }}>▾</span>
              </div>
              {openDropdown === 'tts' && <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpenDropdown(null)} />
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-elevated, #1a1a1a)', border: '1px solid var(--border, rgba(255,255,255,0.1))', borderRadius: 8, marginTop: 4, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                  {ttsKeys.length === 0 && <div style={{ padding: '10px 14px', color: 'var(--text-muted, #888)', fontSize: 13 }}>ยังไม่มี key — เพิ่มด้านล่าง</div>}
                  {ttsKeys.map(k => (
                    <div key={k.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: ttsSelected === k.name ? 'rgba(255,255,255,0.07)' : 'transparent' }}
                      onMouseEnter={e => { if (ttsSelected !== k.name) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.04)' }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ttsSelected === k.name ? 'rgba(255,255,255,0.07)' : 'transparent' }}>
                      <div style={{ flex: 1 }} onClick={() => { setTtsSelected(k.name); setOpenDropdown(null) }}>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{k.nickname ?? k.name}</span>
                        <span style={{ color: 'var(--text-muted, #888)', fontSize: 12, marginLeft: 8 }}>{k.model ?? ''} · {k.preview}</span>
                      </div>
                      <button onClick={e => { e.stopPropagation(); deleteProviderKey(k.name, 'tts') }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted, #666)', cursor: 'pointer', padding: '2px 6px', fontSize: 16, lineHeight: 1, borderRadius: 4 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted, #666)')}>×</button>
                    </div>
                  ))}
                </div>
              </>}
            </div>
            <button className="btn-primary" style={{ whiteSpace: 'nowrap' }}
              onClick={() => ttsSelected && setActiveProviderKey('tts', ttsSelected)}
              disabled={!ttsSelected || ttsSelected === voiceConfig?.ttsActiveKey}>
              Save
            </button>
          </div>

          <div className="settings-add-form">
            <select className="modal-input" value={ttsProviderModel} onChange={e => setTtsProviderModel(e.target.value)}>
              {providers.filter(p => p.category === 'tts').map(p => (
                <option key={p.id} value={p.model}>{p.label} — {p.model}</option>
              ))}
            </select>
            <input className="modal-input" placeholder="Nickname (e.g. Personal Key)" value={ttsNickDraft} onChange={e => setTtsNickDraft(e.target.value)} />
            <input className="modal-input" type="password" placeholder="API Key (AIza...)" value={ttsApiKeyDraft} onChange={e => setTtsApiKeyDraft(e.target.value)} />
            <button className="btn-primary" onClick={() => addProviderKey('tts', ttsNickDraft, ttsApiKeyDraft, ttsProviderModel)} disabled={savingProvider === 'tts'}>
              {savingProvider === 'tts' ? '...' : 'Add Key'}
            </button>
          </div>
        </section>

        {/* ─── STT ────────────────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>STT</h2>
          <p className="settings-hint">API keys สำหรับแปลงเสียงเป็นข้อความ</p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <div className="modal-input" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
                onClick={() => setOpenDropdown(openDropdown === 'stt' ? null : 'stt')}>
                <span style={{ color: sttSelected ? 'inherit' : 'var(--text-muted, #888)' }}>
                  {sttSelected ? (sttKeys.find(k => k.name === sttSelected)?.nickname ?? sttSelected) : '— เลือก key —'}
                  {sttSelected && voiceConfig?.sttActiveKey === sttSelected && <span style={{ color: 'var(--accent, #4ade80)', marginLeft: 8, fontSize: 12 }}>Active ✓</span>}
                </span>
                <span style={{ fontSize: 11, opacity: 0.5 }}>▾</span>
              </div>
              {openDropdown === 'stt' && <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpenDropdown(null)} />
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-elevated, #1a1a1a)', border: '1px solid var(--border, rgba(255,255,255,0.1))', borderRadius: 8, marginTop: 4, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                  {sttKeys.length === 0 && <div style={{ padding: '10px 14px', color: 'var(--text-muted, #888)', fontSize: 13 }}>ยังไม่มี key — เพิ่มด้านล่าง</div>}
                  {sttKeys.map(k => (
                    <div key={k.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: sttSelected === k.name ? 'rgba(255,255,255,0.07)' : 'transparent' }}
                      onMouseEnter={e => { if (sttSelected !== k.name) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.04)' }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = sttSelected === k.name ? 'rgba(255,255,255,0.07)' : 'transparent' }}>
                      <div style={{ flex: 1 }} onClick={() => { setSttSelected(k.name); setOpenDropdown(null) }}>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{k.nickname ?? k.name}</span>
                        <span style={{ color: 'var(--text-muted, #888)', fontSize: 12, marginLeft: 8 }}>{k.model ?? ''} · {k.preview}</span>
                      </div>
                      <button onClick={e => { e.stopPropagation(); deleteProviderKey(k.name, 'stt') }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted, #666)', cursor: 'pointer', padding: '2px 6px', fontSize: 16, lineHeight: 1, borderRadius: 4 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted, #666)')}>×</button>
                    </div>
                  ))}
                </div>
              </>}
            </div>
            <button className="btn-primary" style={{ whiteSpace: 'nowrap' }}
              onClick={() => sttSelected && setActiveProviderKey('stt', sttSelected)}
              disabled={!sttSelected || sttSelected === voiceConfig?.sttActiveKey}>
              Save
            </button>
          </div>

          <div className="settings-add-form">
            <select className="modal-input" value={sttProviderModel} onChange={e => setSttProviderModel(e.target.value)}>
              {providers.filter(p => p.category === 'stt').map(p => (
                <option key={p.id} value={p.model}>{p.label} — {p.model}</option>
              ))}
            </select>
            <input className="modal-input" placeholder="Nickname (e.g. Personal Key)" value={sttNickDraft} onChange={e => setSttNickDraft(e.target.value)} />
            <input className="modal-input" type="password" placeholder="API Key (AIza...)" value={sttApiKeyDraft} onChange={e => setSttApiKeyDraft(e.target.value)} />
            <button className="btn-primary" onClick={() => addProviderKey('stt', sttNickDraft, sttApiKeyDraft, sttProviderModel)} disabled={savingProvider === 'stt'}>
              {savingProvider === 'stt' ? '...' : 'Add Key'}
            </button>
          </div>
        </section>

        {/* ─── Notes directory ────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>Notes</h2>
          <div className="settings-plugin-row">
            <div className="settings-plugin-name">Notes directory</div>
            <div className="settings-plugin-cwd-row">
              <input
                className="modal-input settings-plugin-cwd-input"
                placeholder="$HOME"
                value={notesDirDraft}
                onChange={e => setNotesDirDraft(e.target.value)}
              />
              <button
                className="btn-primary"
                onClick={saveNotesDir}
                disabled={savingVoice === 'notesDir'}
              >
                {savingVoice === 'notesDir' ? '...' : 'Save'}
              </button>
            </div>
            <div className="settings-plugin-meta">
              Root for <code>recall</code> / <code>search_notes</code>.
              {voiceConfig?.notesDir
                ? <>{'  ·  '}Current: <code>{voiceConfig.notesDir}</code></>
                : <>{'  ·  '}Default: <code>$HOME</code></>}
            </div>
          </div>
        </section>
        </>)}

        {activeTab === 'account' && (<>
        {/* ─── Account ────────────────────────────────────────────────────── */}
        <section className="settings-section">
          <h2>Account</h2>
          <p className="settings-hint">
            ออกจากระบบบนเครื่องนี้ (ต้องใส่รหัส TOTP ใหม่เมื่อเข้าใช้อีกครั้ง).
          </p>
          <button
            className="settings-action-btn settings-action-btn--danger"
            onClick={() => { logout(); navigate('/login') }}
          >
            <Icon name="logout" size={16} /> Sign out
          </button>

          <p className="settings-hint" style={{ marginTop: 16 }}>
            ออกจากระบบ "ทุกอุปกรณ์" ทันที (มือถือ, แท็บเบราว์เซอร์, token ที่หลุดไป)
            ทุกเครื่องต้องใส่รหัส TOTP ใหม่ ใช้เมื่อสงสัยว่า token รั่วหรือเปลี่ยนเครื่อง.
          </p>
          <button
            className="settings-action-btn settings-action-btn--danger"
            onClick={async () => {
              if (!confirm('ออกจากระบบทุกอุปกรณ์? ทุกเครื่อง (รวมเครื่องนี้) ต้อง login ใหม่')) return
              try { await api.auth.logoutAll() } catch { /* token ตัวเองตายระหว่างทางก็ถือว่าสำเร็จ */ }
              logout()
              navigate('/login')
            }}
          >
            <Icon name="logout" size={16} /> Sign out all devices
          </button>
        </section>

        {/* ─── API Access (machine API account key) ────────────────────────── */}
        <section className="settings-section">
          <h2>API Access</h2>
          <p className="settings-hint">
            Account key สำหรับเชื่อมต่อผ่าน API (สร้าง / ลิสต์ chat). ส่งเป็น header
            <code> X-Account-Key</code>. แต่ละ chat ที่สร้างจะได้ token ของตัวเองกลับมาอีกที. เก็บเป็นความลับ.
          </p>
          {apiKey ? (
            <>
              <code className="api-key-value" style={{ display: 'block', wordBreak: 'break-all', padding: '8px 10px', background: 'var(--bg-elevated, rgba(127,127,127,0.1))', borderRadius: 6, fontSize: 12 }}>
                {apiKey}
              </code>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button className="settings-action-btn" onClick={() => navigator.clipboard?.writeText(apiKey)}>คัดลอก</button>
                <button className="settings-action-btn" disabled={apiKeyBusy} onClick={genApiKey}>สร้างใหม่</button>
                <button className="settings-action-btn settings-action-btn--danger" disabled={apiKeyBusy} onClick={clearApiKey}>ลบ</button>
              </div>
            </>
          ) : (
            <button className="settings-action-btn" disabled={apiKeyBusy} onClick={genApiKey}>สร้าง API key</button>
          )}
        </section>
        </>)}

        </div>
      </div>
    </div>
  )
}
